import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Popover, Button } from '@material-ui/core';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

function clampDateToYmd(date) {
  if (!(date instanceof Date) || isNaN(date.getTime())) return null;
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function buildIsoFromParts(ymd, hhmm) {
  if (!ymd) return null;
  const [hhRaw, mmRaw] = String(hhmm || '').split(':');
  const hh = Math.max(0, Math.min(23, parseInt(hhRaw, 10) || 0));
  const mm = Math.max(0, Math.min(59, parseInt(mmRaw, 10) || 0));
  const d = new Date(ymd.getFullYear(), ymd.getMonth(), ymd.getDate(), hh, mm, 0);
  return d.toISOString();
}

function parseIsoToLocalYmdAndTime(iso) {
  try {
    if (!iso) return { ymd: null, hhmm: '' };
    const d = new Date(String(iso));
    if (isNaN(d.getTime())) return { ymd: null, hhmm: '' };
    const ymd = new Date(d.getFullYear(), d.getMonth(), d.getDate());
    const hh = String(d.getHours()).padStart(2, '0');
    const mm = String(d.getMinutes()).padStart(2, '0');
    return { ymd, hhmm: `${hh}:${mm}` };
  } catch (e) {
    return { ymd: null, hhmm: '' };
  }
}

function shiftDaysUtc(base, days) {
  const d = new Date(base.getTime() + days * 24 * 60 * 60 * 1000);
  return d;
}

export default function DateRangePickerPopover(props) {
  const {
    open,
    anchorEl,
    onClose,
    maxLookbackDays,
    maxRangeMinutes,
    minIso,
    value,
    onApply,
    onClear,
    disableLast30,
    onAttemptLast30,
    upgradeMessage,
    onDismissUpgradeMessage,
    allowFutureEnd,
  } = props;

  const now = useMemo(() => new Date(), []);

  const minSelectableDate = useMemo(() => {
    const days = Number(maxLookbackDays) || 0;
    const fromLookback = days > 0 ? new Date(now.getTime() - days * 24 * 60 * 60 * 1000) : null;

    let fromEarliest = null;
    try {
      if (minIso) {
        const d = new Date(String(minIso));
        if (!isNaN(d.getTime())) fromEarliest = d;
      }
    } catch (e) {
    }

    if (!fromLookback && !fromEarliest) return null;
    if (!fromLookback) return fromEarliest;
    if (!fromEarliest) return fromLookback;
    return fromLookback.getTime() > fromEarliest.getTime() ? fromLookback : fromEarliest;
  }, [maxLookbackDays, now, minIso]);

  const [startYmd, setStartYmd] = useState(null);
  const [endYmd, setEndYmd] = useState(null);
  const [startTime, setStartTime] = useState('00:00');
  const [endTime, setEndTime] = useState('23:59');

  useEffect(() => {
    const v = value && value.start && value.end ? value : null;
    if (!v) {
      setStartYmd(null);
      setEndYmd(null);
      setStartTime('00:00');
      setEndTime('23:59');
      return;
    }

    const ps = parseIsoToLocalYmdAndTime(v.start);
    const pe = parseIsoToLocalYmdAndTime(v.end);
    setStartYmd(ps.ymd);
    setEndYmd(pe.ymd);
    setStartTime(ps.hhmm || '00:00');
    setEndTime(pe.hhmm || '23:59');
  }, [value]);

  const canApply = useMemo(() => {
    return !!(startYmd && endYmd);
  }, [startYmd, endYmd]);

  const apply = useCallback(() => {
    if (!startYmd || !endYmd) return;

    const startIso = buildIsoFromParts(startYmd, startTime) || null;
    const endIso = buildIsoFromParts(endYmd, endTime) || null;
    if (!startIso || !endIso) return;

    const startMs = Date.parse(startIso);
    const endMs = Date.parse(endIso);
    const nowMs = Date.now();

    let nextStartIso = startIso;
    let nextEndIso = endIso;

    if (Number.isFinite(startMs) && Number.isFinite(endMs) && endMs < startMs) {
      nextEndIso = nextStartIso;
    }

    const nextEndMs = Date.parse(nextEndIso);
    if (!allowFutureEnd) {
      if (Number.isFinite(nextEndMs) && nextEndMs > nowMs) {
        nextEndIso = new Date(nowMs).toISOString();
      }
    }

    if (minSelectableDate) {
      const minMs = minSelectableDate.getTime();
      const nextStartMs = Date.parse(nextStartIso);
      if (Number.isFinite(nextStartMs) && nextStartMs < minMs) {
        nextStartIso = new Date(minMs).toISOString();
      }
    }

    {
      const nextStartMs3 = Date.parse(nextStartIso);
      const nextEndMs3 = Date.parse(nextEndIso);
      if (Number.isFinite(nextStartMs3) && Number.isFinite(nextEndMs3) && nextEndMs3 < nextStartMs3) {
        nextEndIso = nextStartIso;
      }
    }

    const maxMin = Number(maxRangeMinutes);
    if (Number.isFinite(maxMin) && maxMin > 0) {
      const limitMs = maxMin * 60 * 1000;
      const nextStartMs2 = Date.parse(nextStartIso);
      const nextEndMs2 = Date.parse(nextEndIso);
      if (Number.isFinite(nextStartMs2) && Number.isFinite(nextEndMs2)) {
        if (nextEndMs2 - nextStartMs2 > limitMs) {
          nextEndIso = new Date(nextStartMs2 + limitMs).toISOString();
        }
      }
    }

    if (typeof onApply === 'function') onApply({ start: nextStartIso, end: nextEndIso });
    if (typeof onClose === 'function') onClose();
  }, [startYmd, endYmd, startTime, endTime, minSelectableDate, onApply, onClose, allowFutureEnd, maxRangeMinutes]);

  const clear = useCallback(() => {
    if (typeof onClear === 'function') onClear();
    if (typeof onClose === 'function') onClose();
  }, [onClear, onClose]);

  const selectToday = useCallback(() => {
    const d = new Date();
    const ymd = new Date(d.getFullYear(), d.getMonth(), d.getDate());
    setStartYmd(ymd);
    setEndYmd(ymd);
    setStartTime('00:00');
    setEndTime('23:59');
  }, []);

  const selectLastDays = useCallback((days) => {
    const end = new Date();
    const start = shiftDaysUtc(end, -days);
    const startY = new Date(start.getFullYear(), start.getMonth(), start.getDate());
    const endY = new Date(end.getFullYear(), end.getMonth(), end.getDate());
    setStartYmd(startY);
    setEndYmd(endY);
    setStartTime('00:00');
    setEndTime('23:59');
  }, []);

  return (
    <Popover
      open={!!open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      PaperProps={{
        style: {
          background: 'rgba(10,14,30,0.98)',
          border: '1px solid rgba(77, 121, 255, 0.35)',
          borderRadius: 12,
          padding: 10,
          color: '#e5e7ff',
        },
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, minWidth: 520 }}>
        {!!upgradeMessage && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 10,
              padding: '8px 10px',
              borderRadius: 10,
              background: 'rgba(255, 184, 107, 0.12)',
              border: '1px solid rgba(255, 184, 107, 0.35)',
              color: '#ffb86b',
              fontSize: 12,
            }}
          >
            <span>{String(upgradeMessage)}</span>
            <Button
              size="small"
              onClick={() => {
                if (typeof onDismissUpgradeMessage === 'function') onDismissUpgradeMessage();
              }}
              style={{ textTransform: 'none', color: '#ffb86b' }}
            >
              OK
            </Button>
          </div>
        )}

        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <Button variant="outlined" size="small" onClick={selectToday} style={{ textTransform: 'none' }}>
            Today
          </Button>
          <Button variant="outlined" size="small" onClick={() => selectLastDays(7)} style={{ textTransform: 'none' }}>
            Last 7 days
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={() => {
              if (disableLast30) {
                if (typeof onAttemptLast30 === 'function') onAttemptLast30();
                return;
              }
              selectLastDays(30);
            }}
            style={{ textTransform: 'none', opacity: disableLast30 ? 0.5 : 1 }}
          >
            Last 30 days
          </Button>
          <Button variant="outlined" size="small" style={{ textTransform: 'none' }}>
            Custom
          </Button>
        </div>

        <div style={{ display: 'flex', gap: 10 }}>
          <DatePicker
            inline
            monthsShown={2}
            showPreviousMonths
            selectsRange
            startDate={startYmd}
            endDate={endYmd}
            onChange={(dates) => {
              const list = Array.isArray(dates) ? dates : [];
              const s = clampDateToYmd(list[0]);
              const e = clampDateToYmd(list[1]);
              setStartYmd(s);
              setEndYmd(e);
            }}
            minDate={minSelectableDate}
            maxDate={allowFutureEnd ? undefined : new Date()}
          />
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 12, color: '#cfd3ff' }}>From:</span>
            <input
              type="date"
              value={startYmd ? `${startYmd.getFullYear()}-${String(startYmd.getMonth() + 1).padStart(2, '0')}-${String(startYmd.getDate()).padStart(2, '0')}` : ''}
              onChange={(e) => {
                const raw = String(e.target.value || '');
                if (!raw) {
                  setStartYmd(null);
                  return;
                }
                const [yy, mm, dd] = raw.split('-').map((x) => parseInt(x, 10));
                if (!yy || !mm || !dd) return;
                setStartYmd(new Date(yy, mm - 1, dd));
              }}
              style={{ background: '#0b0e1e', color: '#e5e7ff', border: '1px solid rgba(77,121,255,0.45)', borderRadius: 6, padding: '4px 6px' }}
            />
            <input
              type="time"
              value={startTime}
              onChange={(e) => setStartTime(String(e.target.value || '00:00'))}
              style={{ background: '#0b0e1e', color: '#e5e7ff', border: '1px solid rgba(77,121,255,0.45)', borderRadius: 6, padding: '4px 6px' }}
            />
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 12, color: '#cfd3ff' }}>To:</span>
            <input
              type="date"
              value={endYmd ? `${endYmd.getFullYear()}-${String(endYmd.getMonth() + 1).padStart(2, '0')}-${String(endYmd.getDate()).padStart(2, '0')}` : ''}
              onChange={(e) => {
                const raw = String(e.target.value || '');
                if (!raw) {
                  setEndYmd(null);
                  return;
                }
                const [yy, mm, dd] = raw.split('-').map((x) => parseInt(x, 10));
                if (!yy || !mm || !dd) return;
                setEndYmd(new Date(yy, mm - 1, dd));
              }}
              style={{ background: '#0b0e1e', color: '#e5e7ff', border: '1px solid rgba(77,121,255,0.45)', borderRadius: 6, padding: '4px 6px' }}
            />
            <input
              type="time"
              value={endTime}
              onChange={(e) => setEndTime(String(e.target.value || '23:59'))}
              style={{ background: '#0b0e1e', color: '#e5e7ff', border: '1px solid rgba(77,121,255,0.45)', borderRadius: 6, padding: '4px 6px' }}
            />
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
          <Button onClick={clear} size="small" style={{ textTransform: 'none', color: '#cfd3ff' }}>
            Clear
          </Button>
          <Button
            variant="contained"
            size="small"
            onClick={apply}
            disabled={!canApply}
            style={{ textTransform: 'none', backgroundColor: '#4d79ff', color: '#fff' }}
          >
            Apply
          </Button>
        </div>
      </div>
    </Popover>
  );
}
